import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { sanitarywareService } from "../../../services/enhancedCrudService";
import { toast } from "react-toastify";
import {
  MAX_PRODUCT_IMAGE_SIZE,
  MAX_PRODUCT_IMAGE_COUNT,
} from "./../../../shared/constant";
import { Crop } from "./../../../shared/crop";
import Swal from "sweetalert2";

const SanitarywareImages = () => {
  const { product_id } = useParams();
  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isPrimary, setIsPrimary] = useState(false);
  const [croppedFile, setCroppedFile] = useState(null);

  // PDF functionality commented out
  // const [activeTab, setActiveTab] = useState("images");
  // const [pdfs, setPdfs] = useState([]);
  // const [pdfFile, setPdfFile] = useState(null);
  // const [fileName, setFileName] = useState("");
  // const [hideFirm, setHideFirm] = useState(false);
  // const [pdfUploading, setPdfUploading] = useState(false);
  // const [pdfDetailData, setPdfDetailData] = useState(null);

  const fetchProductAndImages = async () => {
    try {
      setLoading(true);
      const productRes = await sanitarywareService.getDetail(product_id);
      const imageRes = await sanitarywareService.getImages(product_id);

      // PDF functionality commented out
      // let pdfData = [];
      // if (productRes && productRes.pdfs && Array.isArray(productRes.pdfs)) {
      //   pdfData = productRes.pdfs;
      // } else {
      //   // Fallback: try separate PDF API call if not in product response
      //   try {
      //     const pdfRes = await sanitarywareService.getPdfs(product_id);
      //     if (Array.isArray(pdfRes)) {
      //       pdfData = pdfRes;
      //     } else if (pdfRes && Array.isArray(pdfRes.data)) {
      //       pdfData = pdfRes.data;
      //     } else if (pdfRes && pdfRes.results && Array.isArray(pdfRes.results)) {
      //       pdfData = pdfRes.results;
      //     } else {
      //       console.log("PDF response structure:", pdfRes);
      //       pdfData = [];
      //     }
      //   } catch (pdfError) {
      //     console.error("Error fetching PDFs:", pdfError);
      //     pdfData = [];
      //   }
      // }

      setProduct(productRes);
      setImages(Array.isArray(imageRes) ? imageRes : []);
      // setPdfs(pdfData); // PDF functionality commented out
    } catch (error) {
      console.error("Error fetching product/images:", error);
      toast.error("Failed to load product or images");
      setImages([]);
      // setPdfs([]); // PDF functionality commented out
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!croppedFile) {
      toast.error("Please select and crop an image to upload");
      return;
    }
    try {
      setUploading(true);
      const form = new FormData();
      form.append("image", croppedFile);
      form.append("label", label || croppedFile.name);
      // Auto-check primary for first image upload
      const shouldBePrimary = images.length === 0 || isPrimary;
      form.append("is_primary", shouldBePrimary ? "true" : "false");
      
      // Upload the image
      await sanitarywareService.uploadImages(product_id, form);
      
      // If this is the first image, call publish API
      if (images.length === 0) {
        try {
          await sanitarywareService.updatePublishStatus(product_id, "publish");
          toast.success("Image uploaded successfully and product published!");
        } catch (publishError) {
          console.error("Publish failed:", publishError);
          toast.warning("Image uploaded successfully, but failed to publish product");
        }
      } else {
        toast.success("Image uploaded successfully!");
      }
      
      setCroppedFile(null);
      setLabel("");
      setIsPrimary(false);
      fetchProductAndImages();
    } catch (error) {
      let message = "Failed to upload image. Please try again.";
      if (error.response && error.response.data) {
        message =
          error.response.data.non_field_errors.length > 0
            ? error.response.data.non_field_errors[0]
            : message;
      }
      toast.error(message);
      console.error("Upload failed:", error.response?.data || error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!imageId) {
      toast.error("Invalid image ID");
      return;
    }
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action will permanently delete the image.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      try {
        await sanitarywareService.deleteImage(product_id, imageId);
        toast.success("Image deleted successfully");
        fetchProductAndImages();
      } catch (error) {
        console.error("Error deleting image:", error);
        
        // Handle specific error messages from API
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          
          // Show advice message first (most helpful for user)
          if (errorData.advice) {
            toast.error(errorData.advice);
            return;
          }
          
          // Show specific error message from API
          if (errorData.error) {
            toast.error(errorData.error);
            return;
          }
          
          // Show message if available
          if (errorData.message) {
            toast.error(errorData.message);
            return;
          }
        }
        
        // Generic error message as fallback
        toast.error("Failed to delete image. Please try again.");
      }
    }
  };

  const handleSetPrimary = async (imageId) => {
    try {
      await sanitarywareService.changePrimaryImage(product_id, imageId);
      toast.success("Primary image updated successfully");
      fetchProductAndImages();
    } catch (error) {
      console.error("Error setting primary image:", error);
      toast.error("Failed to set primary image");
    }
  };

  // PDF functionality commented out
  // const handlePDFUpload = async (e) => {
  //   e.preventDefault();
  //   if (!pdfFile) {
  //     toast.error("Please select a PDF file to upload");
  //     return;
  //   }
  //   if (!fileName.trim()) {
  //     toast.error("Please enter a filename");
  //     return;
  //   }
  //   try {
  //     setPdfUploading(true);
  //     const form = new FormData();
  //     form.append("pdf", pdfFile);
  //     form.append("pdf_name", fileName.trim());
  //     form.append("firm_name_hidden", hideFirm ? "true" : "false");
  //     await sanitarywareService.uploadPdf(product_id, form);
  //     toast.success("PDF uploaded successfully!");
  //     setPdfFile(null);
  //     setFileName("");
  //     setHideFirm(false);
  //     fetchProductAndImages();
  //   } catch (error) {
  //     let message = "Failed to upload PDF. Please try again.";
  //     if (error.response && error.response.data) {
  //       message =
  //         error.response.data.non_field_errors?.length > 0
  //           ? error.response.data.non_field_errors[0]
  //           : message;
  //     }
  //     toast.error(message);
  //     console.error("PDF upload failed:", error.response?.data || error.message);
  //   } finally {
  //     setPdfUploading(false);
  //   }
  // };

  // const handlePDFReplace = async (pdfId) => {
  //   try {
  //     const pdfDetail = await sanitarywareService.getPdfDetail(product_id, pdfId);
  //     setPdfDetailData(pdfDetail);

  //     // Extract meaningful part from existing PDF name
  //     const existingFileName = pdfDetail.pdf_name || "";
  //     const parts = existingFileName.split(/[_\s]+/);
  //     const meaningfulPart = parts.slice(-2).join("_");
  //     setFileName(meaningfulPart || "custom");

  //     setHideFirm(pdfDetail.firm_name_hidden || false);
  //   } catch (error) {
  //     console.error("Error fetching PDF details:", error);
  //     toast.error("Failed to load PDF details");
  //   }
  // };

  // const handlePDFDelete = async (pdfId) => {
  //   const result = await Swal.fire({
  //     title: "Are you sure?",
  //     text: "This action will permanently delete the PDF.",
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonColor: "#d33",
  //     cancelButtonColor: "#3085d6",
  //     confirmButtonText: "Yes, delete it!",
  //   });
  //   if (result.isConfirmed) {
  //     try {
  //       await sanitarywareService.deletePdf(product_id, pdfId);
  //       toast.success("PDF deleted successfully");
  //       fetchProductAndImages();
  //     } catch (error) {
  //       console.error("Error deleting PDF:", error);
  //       toast.error("Failed to delete PDF");
  //     }
  //   }
  // };

  // const handleTabChange = (tab) => {
  //   setActiveTab(tab);
  //   // Reset form states when switching tabs
  //   if (tab === "images") {
  //     setCroppedFile(null);
  //     setLabel("");
  //     setIsPrimary(false);
  //   } else if (tab === "pdfs") {
  //     setPdfFile(null);
  //     setFileName("");
  //     setHideFirm(false);
  //     setPdfDetailData(null);
  //   }
  // };

  useEffect(() => {
    fetchProductAndImages();
  }, [product_id]);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="d-flex justify-content-between align-items-center card-header">
            <h4 className="card-title">
              Manage Product Images - {product?.name}
            </h4>
            <div className="card-actions">
              <Link to={`/products/sanitaryware`} className="btn btn-secondary">
                Back to Product
              </Link>
            </div>
          </div>

          {/* Tab Navigation - PDF functionality commented out */}
          <div className="card-header">
            <ul className="nav nav-tabs card-header-tabs" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link active"
                  type="button"
                >
                  <i className="fas fa-images me-2"></i>
                  Images ({images.length})
                </button>
              </li>
              {/* PDF tab commented out
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === "pdfs" ? "active" : ""}`}
                  onClick={() => handleTabChange("pdfs")}
                  type="button"
                >
                  <i className="fas fa-file-pdf me-2"></i>
                  PDFs ({Array.isArray(pdfs) ? pdfs.length : 0})
                </button>
              </li>
              */}
            </ul>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Images Tab */}
            <div className="tab-pane active">
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="text-dark mb-0" style={{ color: "#6f42c1" }}>
                    Upload New Images
                    <span className="ms-2 badge bg-info">
                      {images.length}/{MAX_PRODUCT_IMAGE_COUNT} Images
                    </span>
                  </h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleUpload}>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group mb-3">
                          <label className="form-label fw-semibold">
                            Image Label
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="imageLabel"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. Front View"
                          />
                          <small className="form-text text-muted">
                            Optional label for the images
                          </small>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group mb-3">
                          <label className="form-label fw-semibold">
                            Upload & Crop Image
                          </label>
                          <div className="d-flex">
                            <Crop
                              cropShape="rect"
                              onChangeCropper={(cropData) => {
                                const { fileCropped, originalFile } = cropData;
                                const croppedFile = new File(
                                  [fileCropped],
                                  originalFile.name,
                                  {
                                    type: originalFile.type,
                                    lastModified: Date.now(),
                                  }
                                );
                                setCroppedFile(croppedFile);
                              }}
                              maxSizeMib={MAX_PRODUCT_IMAGE_SIZE}
                              height={200}
                              width={200}
                              aspectRatio={1}
                              imagePreview={
                                croppedFile
                                  ? URL.createObjectURL(croppedFile)
                                  : null
                              }
                              isButtonHidden={false}
                              guid="single-crop"
                              buttonBackGroundColor="#f8f9fa"
                            />
                          </div>
                          <small className="form-text text-muted">
                            Click to upload and crop image. Supported formats: JPG,
                            PNG, GIF (Max size: {MAX_PRODUCT_IMAGE_SIZE}MB)
                          </small>
                          {croppedFile && (
                            <div className="mt-2">
                              <small className="text-success">
                                <i className="fas fa-check-circle me-1"></i>
                                Image ready for upload: {croppedFile.name}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-check mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isPrimaryCheckbox"
                            checked={images.length === 0 || isPrimary}
                            onChange={(e) => setIsPrimary(e.target.checked)}
                          />
                          <label
                            className="form-check-label fw-semibold"
                            htmlFor="isPrimaryCheckbox"
                          >
                      Mark first image as primary            
                                   </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <button
                            type="submit"
                            className="btn btn-primary w-100"
                            disabled={
                              uploading ||
                              !croppedFile ||
                              images.length >= MAX_PRODUCT_IMAGE_COUNT
                            }
                          >
                            {uploading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Uploading...
                              </>
                            ) : images.length >= MAX_PRODUCT_IMAGE_COUNT ? (
                              "Maximum Images Reached"
                            ) : (
                              "Upload Image and publish"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Images Grid */}
              <div className="card">
                <div className="card-header">
                  <h5>Product Images ({images.length})</h5>
                </div>
                <div className="card-body">
                  {images.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="fas fa-images fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No images uploaded yet</p>
                    </div>
                  ) : (
                    <div className="row">
                      {images.map((image, index) => (
                        <div
                          key={image.id || index}
                          className="col-md-3 col-sm-6 mb-3"
                        >
                          <div className="card">
                            <div className="card-body p-2">
                              <div className="position-relative">
                                <img
                                  src={image.image_url}
                                  alt={image.alt || `Product ${index + 1}`}
                                  className="img-fluid rounded"
                                  style={{
                                    width: "100%",
                                    objectFit: "cover",
                                    aspectRatio: "1/1",
                                  }}
                                />
                                {image.is_primary && (
                                  <div className="position-absolute bottom-0 start-0 w-100">
                                    <div className="bg-success text-white text-center py-1 rounded-bottom">
                                      <small className="fw-bold">
                                        <i className="fas fa-star me-1"></i>
                                        PRIMARY
                                      </small>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="d-flex text-black justify-content-center mt-2">
                                {image.label}
                              </div>
                              <div className="">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="badge badge-info">
                                    #{index + 1}
                                  </span>
                                </div>
                                <div
                                  className="btn-group btn-group-sm w-100 mt-2"
                                  role="group"
                                >
                                  {!image.is_primary && (
                                    <button
                                      className="btn btn-outline-primary"
                                      onClick={() => handleSetPrimary(image.id)}
                                      title="Set as Primary"
                                    >
                                      <i className="fas fa-star"></i>
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDeleteImage(image.id)}
                                    title="Delete"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="card mt-4">
                <div className="card-header">
                  <h5>Image Guidelines</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6>Recommended Specifications:</h6>
                      <ul>
                        <li>Minimum resolution: 800x600 pixels</li>
                        <li>Maximum file size: 5MB per image</li>
                        <li>Supported formats: JPG,JPEG, PNG, GIF,WEBP</li>
                        <li>First image will be the primary image</li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <h6>Best Practices:</h6>
                      <ul>
                        <li>Use high-quality, well-lit images</li>
                        <li>Show product from multiple angles</li>
                        <li>Ensure product is clearly visible</li>
                        <li>Use consistent background/lighting</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SanitarywareImages;